import { useState } from 'react';
import { getTodayInChina } from '../../utils/timestampUtils';
import './BirthYmPicker.css';

/**
 * Year + month selects and a save button. Years run from this year back to 1930
 * (the backend refuses anything outside that) — adults learn here too.
 *
 * `startEmpty`: nothing pre-picked and 保存 waits for both — a default (7 years ago,
 * January) can be saved by accident and reads as a real age (kids songs, 2026-10-15).
 *
 * @param {{ value?: string|null, onSave: (birthYm: string) => Promise<unknown>,
 *           onCancel?: () => void, saveLabel?: string, compact?: boolean, startEmpty?: boolean, idPrefix: string }} props
 */
export default function BirthYmPicker({ value = null, onSave, onCancel, saveLabel = '保存', compact = false, startEmpty = false, idPrefix }) {
  const [thisYear, thisMonth] = getTodayInChina().split('-').map(Number);
  const [initialYear, initialMonth] = value ? value.split('-').map(Number) : startEmpty ? ['', ''] : [thisYear - 7, 1];
  const [year, setYear] = useState(initialYear);
  const [month, setMonth] = useState(initialMonth);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  const years = [];
  for (let y = thisYear; y >= 1930; y--) years.push(y);
  // This year only offers months that have happened (the backend refuses the rest).
  const lastMonth = year === thisYear ? thisMonth : 12;
  const months = Array.from({ length: lastMonth }, (_, i) => i + 1);

  const save = async () => {
    setSaving(true);
    setFailed(false);
    try {
      await onSave(`${year}-${String(month).padStart(2, '0')}`);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`birth-ym-picker${compact ? ' birth-ym-picker--compact' : ''}`}>
      <select
        id={`${idPrefix}-year`}
        aria-label="出生年份"
        value={year}
        onChange={(e) => {
          const y = Number(e.target.value);
          setYear(y);
          if (y === thisYear && month > thisMonth) setMonth(thisMonth);
        }}
      >
        {year === '' && <option value="" disabled>选择年份</option>}
        {years.map((y) => <option key={y} value={y}>{y}年</option>)}
      </select>
      <select id={`${idPrefix}-month`} aria-label="出生月份" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
        {month === '' && <option value="" disabled>月份</option>}
        {months.map((m) => <option key={m} value={m}>{m}月</option>)}
      </select>
      <button type="button" className="birth-ym-picker__save" onClick={save} disabled={saving || year === '' || month === ''}>
        {saving ? '保存中…' : saveLabel}
      </button>
      {onCancel && (
        <button type="button" className="birth-ym-picker__cancel" onClick={onCancel} disabled={saving}>取消</button>
      )}
      {failed && <span className="birth-ym-picker__error" role="alert">没保存成功，请再试一次</span>}
    </div>
  );
}
